import { useEventCallback } from '@cian/react-utils';
import { useDeviceType } from '@cian/ui-kit';
import { useCallback, useMemo } from 'react';
import { FILTER_ALL_VALUE, mapOfferStatusEnums } from 'shared/constants/offersHistory';
import { useAppDispatch } from 'shared/hooks';
import {
  resetListingData,
  setIsFiltersChanged,
  resetFilters,
  setPage,
  setRoomsFilter,
  setStatusFilter,
  setDefaultRooms,
  setCategoriesFilter as setCategories,
  setNextPage,
  setActiveOfferId,
  setViewedOffer,
  setDefaultCategory,
} from 'shared/slices/offersHistory';

import {
  getOffersHistory as fetchOffersHistory,
  openOfferFromHistory,
  getOfferFromHistory as fetchOfferFromHistory,
  IGetOfferFromHistoryParams,
} from 'shared/thunks/offersHistory';
import { IRequestAuthenticationParameters, requestAuthentication } from 'shared/thunks/authentication';
import {
  EOfferStatusRequest,
  TCategoriesFilterOptionValue,
  TRoomsFilterOptionValue,
  TStatusFilterOptionValue,
} from 'shared/types/offersHistory';
import { batch } from 'react-redux';
import {
  trackRoomsFilterChange,
  trackStatusFilterChange,
  trackOfferPreviewShow,
  trackAllOffersButtonClick,
  trackOfferFromHistoryOpen,
} from '../tracking';

export const useOffersHistoryActions = () => {
  const dispatch = useAppDispatch();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === 'desktop';

  const getOffersHistory = useEventCallback((loadMore?: boolean) => {
    dispatch(fetchOffersHistory(loadMore));
  });

  const changeFilters = useEventCallback((isChanged: boolean) => {
    dispatch(setIsFiltersChanged(isChanged));
  });

  const resetAllFilters = useCallback(() => {
    batch(() => {
      dispatch(setActiveOfferId(null));
      dispatch(resetFilters());
      dispatch(resetListingData());
    });
  }, [dispatch]);

  const handleRoomsFilterChange = useEventCallback(async (value: TRoomsFilterOptionValue) => {
    dispatch(setRoomsFilter(getFilterValue(value)));
    dispatch(setPage(1));

    trackRoomsFilterChange(value);

    await dispatch(fetchOffersHistory(false));

    /** Для десктопа сразу после смены категории открываем объявление */
    if (isDesktop) {
      await dispatch(openOfferFromHistory({ isDesktop: true }));
    }
  });

  const handleStatusFilterChange = useEventCallback(async (value: TStatusFilterOptionValue) => {
    dispatch(setDefaultRooms());
    dispatch(setDefaultCategory());
    dispatch(setStatusFilter(getFilterValue(mapOfferStatusEnums[value as EOfferStatusRequest])));
    dispatch(setPage(1));

    trackStatusFilterChange(value);

    await dispatch(fetchOffersHistory(false));

    /** Для десктопа сразу после смены категории открываем объявление */
    if (isDesktop) {
      await dispatch(openOfferFromHistory({ isDesktop: true }));
    }
  });

  const handleCategoriesFilterChange = useEventCallback(async (value: TCategoriesFilterOptionValue) => {
    dispatch(setCategories(getFilterValue(value)));
    dispatch(setPage(1));
    await dispatch(fetchOffersHistory(false));
    /** Для десктопа сразу после смены категории открываем объявление */
    /* istanbul ignore else */
    if (isDesktop) {
      await dispatch(openOfferFromHistory({ isDesktop: true }));
    }
  });

  const handleShowMoreButtonClick = useEventCallback(() => {
    dispatch(setNextPage());
    dispatch(fetchOffersHistory(true));
  });

  const handleResetActiveOffer = useEventCallback(() => {
    dispatch(setActiveOfferId(null));
  });

  const setCategoriesFilter = useEventCallback((value: TCategoriesFilterOptionValue) => {
    dispatch(setCategories(value));
  });

  const openOffer = useEventCallback((params?: IGetOfferFromHistoryParams) => {
    dispatch(openOfferFromHistory({ isDesktop: true, cianId: params?.cianId }));

    trackOfferPreviewShow();
  });

  const handleFullscreenButtonClick = useEventCallback(() => {
    dispatch(openOfferFromHistory({ isDesktop: true }));

    trackAllOffersButtonClick();
  });

  const getOfferFromHistory = useEventCallback((params: IGetOfferFromHistoryParams) => {
    dispatch(fetchOfferFromHistory(params));
  });

  const requestAuthenticationAction = useEventCallback((params: IRequestAuthenticationParameters) => {
    dispatch(requestAuthentication(params));
  });

  const setViewedOfferAction = useEventCallback((offerId: number) => {
    dispatch(setViewedOffer(offerId));
  });

  return useMemo(
    () => ({
      getOffersHistory,
      changeFilters,
      resetAllFilters,
      onRoomsFilterChange: handleRoomsFilterChange,
      onStatusFilterChange: handleStatusFilterChange,
      onCategoriesFilterChange: handleCategoriesFilterChange,
      onShowMoreButtonClick: handleShowMoreButtonClick,
      resetActiveOffer: handleResetActiveOffer,
      setCategoriesFilter,
      onFullscreenButtonClick: handleFullscreenButtonClick,
      openOfferFromHistory: openOffer,
      getOfferFromHistory,
      setViewedOffer: setViewedOfferAction,
      requestAuthentication: requestAuthenticationAction,
      onOfferLinkClick: trackOfferFromHistoryOpen,
    }),
    [
      changeFilters,
      getOfferFromHistory,
      getOffersHistory,
      handleCategoriesFilterChange,
      handleFullscreenButtonClick,
      handleResetActiveOffer,
      handleRoomsFilterChange,
      handleShowMoreButtonClick,
      handleStatusFilterChange,
      openOffer,
      requestAuthenticationAction,
      resetAllFilters,
      setCategoriesFilter,
      setViewedOfferAction,
    ],
  );
};

export function getFilterValue<T>(value: T) {
  return value === FILTER_ALL_VALUE ? FILTER_ALL_VALUE : value;
}
